import { Form, Button, message } from 'antd'
import { NamePath } from 'antd/es/form/interface'
import { FC, ReactNode, useState, useMemo, useEffect } from 'react'
import { EndpointQueryParameters } from '../../../types/strapi/strapi'
import { useCreateContractorMutation } from '../../../store/api/contractors-api'
import { useGetContractorsTypesQuery } from '../../../store/api/contractor-types-api'
import { Rule } from 'antd/es/form'
import AutoComplete, { AutoCompleteOption } from '../../custom-form-controls/auto-complete'
import { PlusOutlined } from '@ant-design/icons'
import { ApprovalStatus } from '../../../types/api/contractor'
import { useScopedContractorsQuery } from '../../../hooks/api/use-scoped-contractors-query'
import { useAppSelector } from '../../../store/store'

interface ClientAutoCompleteProps {
    name?: NamePath
    label?: ReactNode
    rules?: Rule[]
    onChange?: (docId: string) => void
    noStyle?: boolean
    placeholder?: string
    disabled?: boolean
    showDraftOption?: boolean
}
const ClientAutoComplete: FC<ClientAutoCompleteProps> = ({
    name,
    label,
    rules,
    onChange,
    noStyle,
    placeholder = 'Клиент',
    disabled,
}) => {
    const [searchValue, setSearchValue] = useState<string>('')
    const [debouncedSearchValue, setDebouncedSearchValue] = useState<string>('')
    const user = useAppSelector(state => state.auth.user)

    const { data: contractorTypesData } = useGetContractorsTypesQuery()
    const [createContractor, { isLoading: isCreatingContractor }] = useCreateContractorMutation()
    const clientType = contractorTypesData?.data?.find(type => type.name === 'Клиент')

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchValue(searchValue)
        }, 300)

        return () => clearTimeout(timer)
    }, [searchValue])

    const queryParams: EndpointQueryParameters = useMemo(() => {
        const filters: any = {
            type: { name: { $contains: 'Клиент' } },
        }

        if (debouncedSearchValue.trim()) {
            filters.name = { $containsi: debouncedSearchValue.trim() }
        }

        return {
            filters,
            pagination: { page: 1, pageSize: 50 },
        }
    }, [debouncedSearchValue])

    const { data: contractorsData, isLoading: isLoadingContractors } =
        useScopedContractorsQuery(queryParams)

    const options: AutoCompleteOption[] = useMemo(() => {
        if (!contractorsData?.data) return []

        return contractorsData.data.map(contractor => ({
            value: contractor.name,
            label: contractor.name,
            key: contractor.documentId,
        }))
    }, [contractorsData])

    const handleSearch = (value: string) => {
        setSearchValue(value)
    }

    const handleSelect = (value: string) => {
        const selectedContractor = contractorsData?.data?.find(
            contractor => contractor.name === value
        )

        if (selectedContractor && onChange) {
            setSearchValue(value)
            onChange(selectedContractor.documentId)
        }
    }

    const handleCreateClient = async () => {
        if (!searchValue.trim()) {
            message.warning('Введите имя клиента')
            return
        }
        if (!clientType) {
            message.error('Не найден тип "Клиент"')
            return
        }
        if (!user?.documentId) {
            message.error('Не удалось определить текущего пользователя')
            return
        }
        try {
            const result = await createContractor({
                data: {
                    name: searchValue.trim(),
                    type: { connect: [clientType.documentId] },
                    approvalStatus: 'draft' as ApprovalStatus,
                    defermentInDays: 0,
                    curators: [
                        {
                            user: { connect: [user.documentId] },
                            percent: 3.5,
                        },
                    ],
                } as any,
            }).unwrap()
            message.success('Клиент успешно создан')

            if (onChange) {
                onChange(result.data.documentId)
            }

            setSearchValue('')
            setDebouncedSearchValue('')
        } catch (error) {
            message.error('Ошибка при создании клиента')
            console.error('Error creating client:', error)
        }
    }
    const handleChange = (value: string) => {
        setSearchValue(value)
    }
    const existingClient = contractorsData?.data?.find(
        contractor => contractor.name.toLowerCase() === searchValue.trim().toLowerCase()
    )
    const showCreateButton =
        searchValue.trim() && !existingClient && !isCreatingContractor && !isLoadingContractors
    return (
        <Form.Item name={name} label={label} rules={rules} noStyle={noStyle}>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <AutoComplete
                    style={{ flex: 1 }}
                    options={options}
                    value={searchValue}
                    onChange={handleChange}
                    onSearch={handleSearch}
                    onSelect={handleSelect}
                    placeholder={placeholder}
                    disabled={disabled}
                />
                {showCreateButton && (
                    <Button
                        type='primary'
                        icon={<PlusOutlined />}
                        onClick={handleCreateClient}
                        loading={isCreatingContractor}
                        size='small'
                        title={`Создать клиента "${searchValue}"`}
                    >
                        Создать
                    </Button>
                )}
            </div>
        </Form.Item>
    )
}
export default ClientAutoComplete
